// iter-121.116 Phase 4 — Reusable KPI card with sparkline + delta.
//
// Used by the SA Cockpit Information tabs. Renders one of the per-tab
// cells returned by `/api/home/cockpit/kpi-hero` (4 per tab).
//
// Format types supported (from backend `kpi_hero.cells[].format`):
//   currency  → "$1,234.56"
//   int       → "1,234"
//   number    → "12.5"
//   percent   → "99.95%"
//   duration_ms → "2.1s" (or "215ms" when < 1s)
//   rows      → "842K" rows
//   delta     → "+12.5%" (used by MRR-growth-style cells)
import React from "react";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

function formatValue(value, format) {
    if (value == null || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    switch (format) {
        case "currency":
            return num >= 1000000
                ? `$${(num / 1000000).toFixed(2)}M`
                : num >= 1000
                    ? `$${(num / 1000).toFixed(1)}K`
                    : `$${num.toFixed(2)}`;
        case "int":
            if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
            if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
            return num.toLocaleString();
        case "number":
            return num.toLocaleString(undefined, { maximumFractionDigits: 1 });
        case "percent":
            return `${num.toFixed(2)}%`;
        case "duration_ms":
            return num >= 1000 ? `${(num / 1000).toFixed(2)}s` : `${Math.round(num)}ms`;
        case "rows":
            if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M rows`;
            if (num >= 1000) return `${(num / 1000).toFixed(1)}K rows`;
            return `${num} rows`;
        case "delta":
            return `${num >= 0 ? "+" : ""}${num.toFixed(2)}%`;
        default:
            return num.toLocaleString();
    }
}

function DeltaBadge({ deltaPct }) {
    if (deltaPct == null) {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground"
                  data-testid="kpi-delta-na">
                <Minus className="h-3 w-3" /> n/a
            </span>
        );
    }
    const positive = deltaPct >= 0;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider ${positive ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}
              data-testid={positive ? "kpi-delta-up" : "kpi-delta-down"}>
            {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {positive ? "+" : ""}{deltaPct.toFixed(1)}%
        </span>
    );
}

function Sparkline({ data, positive }) {
    if (!data || data.length === 0) {
        return <div className="h-8 w-full" data-testid="kpi-sparkline-empty" />;
    }
    const stroke = positive
        ? "var(--emerald-500, #10b981)"
        : "var(--destructive, #ef4444)";
    return (
        <div className="h-8 w-full min-w-[40px]" data-testid="kpi-sparkline">
            <ResponsiveContainer width="100%" height="100%" minWidth={40} minHeight={20}>
                <AreaChart data={data} margin={{ top: 1, right: 0, bottom: 1, left: 0 }}>
                    <defs>
                        <linearGradient id="kpiSparkFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%"   stopColor={stroke} stopOpacity={0.30} />
                            <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <Area
                        type="monotone"
                        dataKey="v"
                        stroke={stroke}
                        strokeWidth={1.5}
                        fill="url(#kpiSparkFill)"
                        isAnimationActive={false}
                        dot={false}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}

export default function KpiCard({ cell }) {
    if (!cell) return null;
    const positive = (cell.delta_pct ?? 0) >= 0;
    return (
        <div className="border border-border rounded-sm bg-card p-4 flex flex-col gap-2"
             data-testid={`kpi-card-${cell.key}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="eyebrow text-[10px] truncate">{cell.label}</div>
                <DeltaBadge deltaPct={cell.delta_pct} />
            </div>
            <div className="kpi-number text-3xl leading-tight font-semibold"
                 data-testid={`kpi-card-value-${cell.key}`}>
                {formatValue(cell.value, cell.format)}
            </div>
            {cell.secondary && (
                <div className="text-[11px] text-muted-foreground">
                    {cell.secondary.label}:{" "}
                    <span className="text-foreground font-medium">
                        {typeof cell.secondary.value === "number"
                            ? cell.secondary.value.toLocaleString()
                            : cell.secondary.value}
                    </span>
                </div>
            )}
            <Sparkline data={cell.sparkline} positive={positive} />
            {cell.note && (
                <div className="text-[10px] text-muted-foreground italic">{cell.note}</div>
            )}
        </div>
    );
}
